/// <amd-module name="VSS/Redux/Deprecated/RootProvider" />
import * as React from "react";
import { Provider } from "react-redux";
import { DeepPartial } from "redux";
import { createStore, IModule, IModuleStore } from "redux-dynamic-modules";
import { getSagaExtension } from "redux-dynamic-modules-saga";
import { getHubViewStateModule } from "./HubViewState/HubViewStateModule";
import { NavigationModule } from "VSS/Redux/Modules/Navigation";
import { getTelemetryModule } from "VSS/Redux/Modules/Telemetry";
import { getTTIMonitorModule } from "VSS/Redux/Modules/TTIMonitor";
import { VssHubViewState } from "VSSUI/Navigation/VssHubViewState";
import { VSSPageDataModule } from "VSS/Redux/Modules/VSSPageData";
import { QueuedActionsModule } from "VSS/Redux/Modules/QueuedActions";

export interface IRootProviderProps<SagaContext, State> {
    /**
     * Initial state for the store
     */
    initialState: DeepPartial<State>;
    /**
     * Context for the sagas
     */
    sagaContext: SagaContext;
    /**
     * Scenario name to used for telemetry
     */
    scenarioName: string;
    /**
     * Initial set of modules to load in the store
     */
    modules: IModule<any>[];
    /**
     * Split timing name to use for TTI after loading the store
     */
    ttiSplitTimingName?: string;

    /**
     * To track the hub view state and loads the HubViewState module
     */
    hubViewState?: VssHubViewState;
}

/**
 * Should be used as the root reducer
 * This loads some common modules e.g. telemetry, tti, hub view state
 * Also disposes the store automatically on unmount
 */
export class RootProvider<SagaContext, State> extends React.Component<IRootProviderProps<SagaContext, State>> {
    private _store?: IModuleStore<any>;
    private _caughtSagaError: Error;
    private _mounted: boolean = false;

    constructor(props: IRootProviderProps<SagaContext, State>, context: any) {
        super(props, context);
        const { initialState, sagaContext, modules, scenarioName, hubViewState, ttiSplitTimingName } = this.props;
        this.state = {};

        const allModules: IModule<any>[] = [
            NavigationModule,
            getTelemetryModule(scenarioName),
            getTTIMonitorModule(ttiSplitTimingName),
            VSSPageDataModule,
            QueuedActionsModule
        ];

        if (hubViewState) {
            allModules.push(getHubViewStateModule(hubViewState));
        }

        allModules.push(...modules);

        this._store = createStore({
            initialState, 
            enhancers: [],
            extensions: [getSagaExtension(sagaContext, this._onSagaError)]
        }, ...allModules);
    }

    public render() {
        this._throwIfSagaFailed();

        return <Provider store={this._store!}>{this.props.children}</Provider>;
    }

    public componentWillUnmount() {
        if (this._store) {
            this._store.dispose();
        }
        this._store = undefined;
    }

    public componentDidMount() {
        this._mounted = true;
        this._throwIfSagaFailed();
    }

    public componentDidUpdate() {
        this._throwIfSagaFailed();
    }

    /**
     * When the saga middleware gets an unhandled exception,
     * assign it to the state so it can be thrown in the React context
     */
    private _onSagaError = (caughtSagaError: Error): void => {
        this._caughtSagaError = caughtSagaError;
        if (this._mounted) {
            this.forceUpdate();
        }
    };

    private _throwIfSagaFailed(): void {
        // If there was a caught error, throw it now
        if (this._caughtSagaError) {
            throw this._caughtSagaError;
        }
    }
}
